import React, { useState, useEffect } from 'react';
import { 
  Form, Input, Select, Button, Space, message, Modal, Card, Tag, Row, Col, 
  Pagination, Typography, DatePicker, Flex, Divider, Spin 
} from 'antd';
import { 
  SearchOutlined, ReloadOutlined, DeleteOutlined, PlusOutlined, 
  EditOutlined, FilterOutlined, EyeOutlined, CloseCircleOutlined, ExclamationCircleOutlined 
} from '@ant-design/icons';
import './index.css';
import apiClient from '../../utils/apiService';
import moment from 'moment'; // 添加 moment 导入

const { Option } = Select;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

function BlacklistManage() {
  const [blacklistData, setBlacklistData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 12,
    total: 0
  });
  const [filters, setFilters] = useState({
    companyName: '',
    address: '',
    phone: '',
    email: '',
    date: '',
    createdBy: ''
  });
  const [visible, setVisible] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [form] = Form.useForm();
  const [operationType, setOperationType] = useState('add'); // 'add' or 'edit'
  const [messageApi, contextHolder] = message.useMessage();
  const [myModal, modalcontextHolder] = Modal.useModal();

  // Load blacklist data
  const loadBlacklistData = async (page = 1, pageSize = 10, filterParams = {}) => {
    setLoading(true);
    try {
      const params = {
        page: page,
        size: pageSize,
        ...filterParams
      };

      const response = await apiClient.post('/blacklist/page', params);

      if (response.data && response.data.code === 200) {
        const { records, total, page: currentPage, size: currentSize } = response.data.data;
        setBlacklistData(records || []);
        setPagination({
          current: currentPage,
          pageSize: currentSize,
          total: total
        });
      } else {
        messageApi.error(response.data?.message || 'Failed to load data');
      }
    } catch (error) {
      console.error('Error loading blacklist data:', error);
      messageApi.error('Failed to load blacklist data');
    } finally {
      setLoading(false);
    }
  };

  // Handle pagination change
  const handlePageChange = (page, pageSize) => {
    loadBlacklistData(page, pageSize, filters);
  };

  // Handle filter change
  const handleFilterChange = (changedValues) => {
    const newFilters = { ...filters, ...changedValues };
    setFilters(newFilters);
  };

  // Handle search
  const handleSearch = () => {
    loadBlacklistData(1, pagination.pageSize, filters);
  };

  // Reset filters
  const handleReset = () => {
    setFilters({
      companyName: '',
      address: '',
      phone: '',
      email: '',
      date: '',
      createdBy: ''
    });
    form.resetFields();
    loadBlacklistData(1, pagination.pageSize, {});
  };

  // Open modal for add/edit
  const showModal = (record = null, type = 'add') => {
    setOperationType(type);
    setCurrentRecord(record);
    setVisible(true);
    if (record) {
      form.setFieldsValue({
        ...record,
        date: record.date ? moment(record.date, 'MM/DD/YYYY') : undefined
      });
    } else {
      form.resetFields();
    }
  };

  // Close modal
  const handleCancel = () => {
    setVisible(false);
    form.resetFields();
  };

  // Submit form
  const handleSubmit = async (values) => {
    setConfirmLoading(true);
    try {
      let response;
      if (operationType === 'add') {
        response = await apiClient.post('/blacklist/add', values);
      } else {
        response = await apiClient.put('/blacklist/update', {
          id: currentRecord.id,
          ...values
        });
      }

      if (response.data && response.data.code === 200) {
        messageApi.success(operationType === 'add' ? 'Added successfully' : 'Updated successfully');
        setVisible(false);
        form.resetFields();
        loadBlacklistData(pagination.current, pagination.pageSize, filters);
      } else {
        messageApi.error(response.data?.message || 'Operation failed');
      }
    } catch (error) {
      console.error(`Failed to ${operationType} blacklist record:`, error);
      messageApi.error(`Failed to ${operationType} blacklist record`);
    } finally {
      setConfirmLoading(false);
    }
  };

  // Delete record
  const handleDelete = async (id) => {
    try {
      const response = await apiClient.delete(`/blacklist/delete/${id}`);
      if (response.data && response.data.code === 200) {
        messageApi.success('Deleted successfully');
        loadBlacklistData(pagination.current, pagination.pageSize, filters);
      } else {
        messageApi.error(response.data?.message || 'Delete failed');
      }
    } catch (error) {
      console.error('Failed to delete blacklist record:', error);
      messageApi.error('Failed to delete blacklist record');
    }
  };


  // Get detail record
  const getDetail = async (id) => {
    try {
      const response = await apiClient.get(`/blacklist/detail/${id}`);
      if (response.data && response.data.code === 200) {
        return response.data.data;
      } else {
        throw new Error(response.data?.message || 'Failed to get detail');
      }
    } catch (error) {
      console.error('Failed to get detail:', error);
      throw error;
    }
  };

  // Initialize data
  useEffect(() => {
    loadBlacklistData();
  }, []);

  // Filter form
  const filterForm = (
    <Card className="filter-card search-form" style={{ marginBottom: 16, padding: '24px' }}>
      <Form
        form={form}
        layout="vertical"
      >
        <div className="form-items">
          <Form.Item label="Company Name" name="companyName">
            <Input
              placeholder="Enter company name"
              value={filters.companyName}
              onChange={(e) => handleFilterChange({ companyName: e.target.value })}
              allowClear
            />
          </Form.Item>

          <Form.Item label="Address" name="address">
            <Input
              placeholder="Enter address"
              value={filters.address}
              onChange={(e) => handleFilterChange({ address: e.target.value })}
              allowClear
            />
          </Form.Item>

          <Form.Item label="Phone" name="phone">
            <Input
              placeholder="Enter phone"
              value={filters.phone}
              onChange={(e) => handleFilterChange({ phone: e.target.value })}
              allowClear
            />
          </Form.Item>

          <Form.Item label="Email" name="email">
            <Input
              placeholder="Enter email"
              value={filters.email}
              onChange={(e) => handleFilterChange({ email: e.target.value })}
              allowClear
            />
          </Form.Item>


          <Form.Item label="Date" name="date">
            <DatePicker
              value={filters.date ? moment(filters.date, 'MM/DD/YYYY') : null}
              onChange={(date) => handleFilterChange({
                date: date ? date.format('MM/DD/YYYY') : ''
              })}
              format="MM/DD/YYYY"
              allowClear
            />
          </Form.Item>

          <Form.Item label="Created By" name="createdBy">
            <Input
              placeholder="Enter creator"
              value={filters.createdBy}
              onChange={(e) => handleFilterChange({ createdBy: e.target.value })}
              allowClear
            />
          </Form.Item>
        </div>


        {/* <Divider /> */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-start' }}>
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
            Search
          </Button>
          <Button icon={<ReloadOutlined />} onClick={handleReset}>
            Reset
          </Button>
        </div>
      </Form>
    </Card>
  );

  // Toolbar with Add button
  const toolbar = (
    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Title level={4} style={{ margin: 0 }}>Blacklist Records ({pagination.total})</Title>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => showModal(null, 'add')}
      >
        Add New
      </Button>
    </div>
  );

  return (
    <div className="blacklistManage-container">
      {contextHolder}
      {modalcontextHolder}
      {filterForm}
      {toolbar}

      <div 
        className="blacklist-cards-container"
        style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          {loading ? (
            <Col span={24} style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spin size="large" />
            </Col>
          ) : blacklistData.length > 0 ? (
            blacklistData.map((record) => (
              <Col xs={24} sm={24} md={12} lg={8} xl={6} key={record.id}>
                <div className="blacklist-card" style={{
                  border: '1px solid #e8eaec',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  transition: 'all 0.2s ease',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  backgroundColor: '#ffffff',
                }}>
                  <div style={{
                    padding: '12px',
                    borderBottom: '1px solid #e8eaec',
                    backgroundColor: '#f6f8fa',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <div style={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      backgroundColor: '#ffebee',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: 8
                    }}>
                      <CloseCircleOutlined style={{ fontSize: 16, color: '#e53935' }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: '14px', color: '#1f2329' }}>
                        {record.companyName}
                        <Tag color="red" style={{ marginLeft: 8, height: '20px', lineHeight: '18px', fontSize: '11px' }}>BLACKLIST</Tag>
                      </div>
                    </div>
                  </div>
                  <div style={{ padding: '12px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ fontSize: '12px', color: '#656d76', marginBottom: '4px' }}>
                      <span><strong>Phone:</strong> {record.phone || '-'}</span>
                    </div>
                    <div style={{ fontSize: '12px', color: '#656d76', marginBottom: '8px' }}>
                      <strong>Email:</strong> {record.email || '-'}
                    </div>
                    <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px dashed #e8eaec' }}>
                      <div style={{ fontSize: '11px', color: '#656d76' }}>
                        <strong>Note:</strong> {record.note || '-'}
                      </div>
                    </div>
                  </div>
                  <div style={{
                    display: 'flex',
                    borderTop: '1px solid #e8eaec',
                    backgroundColor: '#f6f8fa',
                  }}>
                    <div style={{ flex: 1, textAlign: 'center', borderRight: '1px solid #e8eaec' }}>
                      <Button 
                        type="text" 
                        size="small"
                        icon={<EyeOutlined />} 
                        onClick={() => getDetail(record.id).then(detail => {
                          myModal.info({
                            title: 'Blacklist Detail',
                            width: 600,
                            content: (
                              <div>
                                <div style={{ marginBottom: 8 }}>
                                  <strong>Company Name:</strong> {detail.companyName || '-'}
                                </div>
                                <div style={{ marginBottom: 8 }}>
                                  <strong>Address:</strong> {detail.address || '-'}
                                </div>
                                <div style={{ marginBottom: 8 }}>
                                  <strong>Phone:</strong> {detail.phone || '-'}
                                </div>
                                <div style={{ marginBottom: 8 }}>
                                  <strong>Email:</strong> {detail.email || '-'}
                                </div>
                                <div style={{ marginBottom: 8 }}>
                                  <strong>Date:</strong> {detail.date || '-'}
                                </div>
                                <div style={{ marginBottom: 8 }}>
                                  <strong>Note:</strong> {detail.note || '-'}
                                </div>
                                <div style={{ marginBottom: 8 }}>
                                  <strong>Created By:</strong> {detail.createdBy || '-'}
                                </div>
                              </div>
                            ),
                            okText: 'OK'
                          });
                        }).catch(error => {
                          messageApi.error('Failed to load detail: ' + error.message);
                        })}
                        style={{ width: '100%', height: '32px', padding: 0 }}
                      />
                    </div>
                    <div style={{ flex: 1, textAlign: 'center', borderRight: '1px solid #e8eaec' }}>
                      <Button 
                        type="text" 
                        size="small"
                        icon={<EditOutlined />} 
                        onClick={() => showModal(record, 'edit')}
                        style={{ width: '100%', height: '32px', padding: 0 }}
                      />
                    </div>
                    <div style={{ flex: 1, textAlign: 'center' }}>
                      <Button 
                        type="text" 
                        size="small"
                        danger
                        icon={<DeleteOutlined />} 
                        onClick={() => {
                          myModal.confirm({
                            title: 'Confirm Deletion',
                            icon: <ExclamationCircleOutlined />,
                            content: `Are you sure you want to delete the blacklist record for "${record.companyName}"?`,
                            okText: 'Yes',
                            okType: 'danger',
                            cancelText: 'No',
                            onOk: () => handleDelete(record.id)
                          });
                        }}
                        style={{ width: '100%', height: '32px', padding: 0 }}
                      />
                    </div>
                  </div>
                </div>
              </Col>
            ))
          ) : (
            <Col span={24} style={{ textAlign: 'center', padding: '40px 0' }}>
              No blacklist records found
            </Col>
          )}
        </Row>
      </div>

      {pagination.total > 0 && (
        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            onChange={handlePageChange}
            showSizeChanger
            showQuickJumper
            showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
          />
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal
        title={operationType === 'add' ? 'Add Blacklist Record' : 'Edit Blacklist Record'}
        open={visible}
        confirmLoading={confirmLoading}
        onCancel={handleCancel}
        width={700}
        styles={{ body: { maxHeight: '60vh', overflowY: 'auto' } }}
        footer={[
          <Button key="back" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={confirmLoading}
            onClick={() => form.submit()}
          >
            {operationType === 'add' ? 'Add' : 'Update'}
          </Button>,
        ]}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="companyName"
            label="Company Name"
            rules={[{ required: true, message: 'Please input company name!' }]}
          >
            <Input placeholder="Enter company name" />
          </Form.Item>

          <Form.Item name="address" label="Address">
            <Input.TextArea rows={2} placeholder="Enter company address" />
          </Form.Item>

          <Form.Item name="phone" label="Phone">
            <Input placeholder="Enter phone number" />
          </Form.Item>

          <Form.Item name="email" label="Email">
            <Input placeholder="Enter email address" />
          </Form.Item>

          <Form.Item name="note" label="Note">
            <Input.TextArea rows={3} placeholder="Enter note" />
          </Form.Item>

          <Form.Item name="date" label="Date">
            <DatePicker
              style={{ width: '100%' }}
              placeholder="Select date"
              format="MM/DD/YYYY"
            />
          </Form.Item>

          
          <Form.Item
            name="createdBy"
            label="Created By"
            rules={[{ required: true, message: 'Please input creator!' }]}
          >
            <Input placeholder="Enter creator name" />
          </Form.Item>
         

          
        </Form>
      </Modal>
    </div>
  );
}

export default BlacklistManage;